import type { ReactNode } from "react";

import { cn } from "@reactive-resume/utils";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  KPI CARD (consoles opérationnelles admin)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Pastille de mesure d'une file d'attente : libellé discret, valeur forte,
 *  icône teintée. La couleur porte un SENS (niveau d'urgence), jamais une
 *  décoration — un opérateur doit lire l'état de ses files d'un seul regard.
 *  Design system admin : surface + border `admin-*`, coins `rounded-xl-admin`.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const TONES = {
  amber: "bg-amber-500/10 text-amber-600 ",
  sky: "bg-sky-500/10 text-sky-600 ",
  red: "bg-red-500/10 text-red-600 ",
  emerald: "bg-emerald-500/10 text-emerald-600 ",
  violet: "bg-violet-500/10 text-violet-600 ",
  neutral: "bg-admin-surface-elevated text-admin-text-secondary",
} as const;

export type KpiTone = keyof typeof TONES;

export type KpiCardProps = {
  icon: ReactNode;
  label: string;
  value: string | number;
  tone?: KpiTone;
  /** Précision contextuelle sous la valeur (ex. « 320 000 Ar en attente »). */
  hint?: string;
  /** Carte-cliquable : sert de filtre de file d'attente. */
  onClick?: () => void;
  className?: string;
};

export const KpiCard = ({
  icon,
  label,
  value,
  tone = "neutral",
  hint,
  onClick,
  className,
}: KpiCardProps) => {
  const Root = onClick ? "button" : "div";

  return (
    <Root
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "flex items-center justify-between gap-4 rounded-xl-admin border border-admin-border bg-admin-surface p-5 text-left shadow-sm-admin",
        onClick && "transition-colors hover:bg-admin-surface-hover",
        className,
      )}
    >
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-admin-text-muted">{label}</span>
        <span className="text-3xl font-bold text-admin-text-primary">{value}</span>
        {hint && <span className="text-xs text-admin-text-muted">{hint}</span>}
      </div>

      <span className={cn("grid size-10 shrink-0 place-items-center rounded-xl", TONES[tone])}>
        {icon}
      </span>
    </Root>
  );
};
